import { ClearOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Col,
  Divider,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppContext from "../../../context/app/context";
import FieldBuilder from "../../../components/form-components/field-builder";
import Form from "../../../components/form-components/from";
import PageHeader from "../../../components/general/layout/page-header";
import SearchFilterCard from "../../../components/general/filter-card";
import RefreshBtn from "../../../components/general/table-components/actions/refresh-btn";
import Table from "../../../components/general/table-components/table";
import {
  INVENTORY_SHORTAGE_NOTES_MAX_LENGTH,
  INVENTORY_SHORTAGE_NOTES_MIN_LENGTH,
  InventoryShortageErrorKey,
  InventoryShortageStatus,
  getInventoryShortageCoverageTypeLabel,
  getInventoryShortageErrorLabel,
  getInventoryShortageStatusColor,
  getInventoryShortageStatusLabel,
  getInventoryShortageUnavailableReasonLabel,
} from "../../../models/wms/inventory-shortage/enum";
import {
  CashCheckVerificationMethod,
  getCashCheckVerificationMethodColor,
  getCashCheckVerificationMethodName,
} from "../../../models/order/cash-check";
import { IBatch } from "../../../models/wms/batch/response";
import { IInventoryShortageQuery } from "../../../models/wms/inventory-shortage/query";
import {
  IInventoryShortage,
  IInventoryShortageReceiptCorrectionBatch,
} from "../../../models/wms/inventory-shortage/response";
import {
  mapInventoryShortageResource,
  normalizeInventoryShortageListingResponse,
} from "../../../models/wms/inventory-shortage/mapper";
import EndPoints from "../../../services/end-points";
import IBaseListingResponse from "../../../utils/api/base-listing-response";
import { createUuidV4 } from "../../../utils/helpers/uuid";
import {
  formatWeightFromGrams,
  kilogramsToGrams,
} from "../../../utils/helpers/weight";
import styles from "./style.module.scss";

const buildEmptyQuery = (perPage = 15): IInventoryShortageQuery => ({
  page: 1,
  perPage,
  status: undefined,
  orderId: undefined,
  productId: undefined,
  storeId: undefined,
  keyword: undefined,
  unresolvedOnly: undefined,
});

const toNumberOrUndefined = (value: any) => {
  if (value === undefined || value === null || value === "") return undefined;
  return Number(value);
};

const getLocalizedName = (
  name: string | { ar?: string | null; en?: string | null } | null | undefined,
  language: string,
) => {
  if (!name) return "-";
  if (typeof name === "string") return name;
  return language.startsWith("ar")
    ? name.ar || name.en || "-"
    : name.en || name.ar || "-";
};

const getErrorKey = (error: any): string | undefined =>
  error?.response?.data?.errorKey;

const getErrorText = (error: any): string | undefined => {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && typeof errors[0] === "string"
    ? errors[0]
    : undefined;
};

const getErrorContext = (error: any): Record<string, any> =>
  error?.response?.data?.context ?? {};

/**
 * No response, a timeout or a 5xx means the request may or may not have been
 * applied. Only these deserve a replay of the identical token — a 422 is a
 * decided refusal and must not be resent under the same token.
 */
const isUndecidedRequestError = (error: any): boolean => {
  const status = error?.response?.status;
  return !status || status === 408 || (status >= 500 && status <= 599);
};

type ResolutionType = "cover" | "correction";

/**
 * A batch as the resolution dialogs need it. The two dialogs read different
 * sources — a correction may only name a batch the line was allocated from,
 * while a cover may name any batch of the same product and store — so both are
 * normalised into one shape here.
 */
interface IResolutionBatchOption {
  batchId: number;
  batchNumber?: string | null;
  expirationDate?: string | null;
  purchaseDate: string;
  /** Integer grams. */
  availableQuantityPhysical: number;
  allocatedQuantityPhysical?: number;
}

/**
 * `/wms/batches` exposes three availability fields. `availableQuantity` is the
 * integer-gram figure the server itself compares a cover against;
 * `availableQuantityAccounting` is a duplicate of it, and
 * `availableQuantityByUnit` is decimal kilograms. Only the first is correct here.
 */
const fromStoreBatch = (batch: IBatch): IResolutionBatchOption => ({
  batchId: batch.id,
  batchNumber: batch.batchNumber ?? null,
  expirationDate: batch.expirationDate ?? null,
  purchaseDate: batch.purchaseDate,
  availableQuantityPhysical: batch.availableQuantity,
});

const fromAllocatedBatch = (
  batch: IInventoryShortageReceiptCorrectionBatch,
): IResolutionBatchOption => ({ ...batch });

/** Missing or unparseable dates sort last. */
const expirationRank = (date?: string | null) => {
  const time = date ? moment(date).valueOf() : NaN;
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

/**
 * Which batch the cover dialog opens on. Among the batches this line was
 * allocated from that still hold stock, the one that supplied the most, then the
 * one expiring soonest.
 *
 * An empty intersection yields no default deliberately: if no allocated batch
 * has stock left, the extra weight most likely came from a different batch
 * altogether, and preselecting one would steer the operator toward the answer
 * more likely to be wrong.
 */
const pickDefaultCoverBatch = (
  allocated: IInventoryShortageReceiptCorrectionBatch[],
  inStock: IResolutionBatchOption[],
): number | undefined => {
  const inStockIds = new Set(inStock.map((batch) => batch.batchId));
  const candidates = allocated.filter((batch) => inStockIds.has(batch.batchId));
  if (!candidates.length) return undefined;

  return candidates.reduce((winner, candidate) => {
    if (
      candidate.allocatedQuantityPhysical !== winner.allocatedQuantityPhysical
    ) {
      return candidate.allocatedQuantityPhysical >
        winner.allocatedQuantityPhysical
        ? candidate
        : winner;
    }
    return expirationRank(candidate.expirationDate) <
      expirationRank(winner.expirationDate)
      ? candidate
      : winner;
  }).batchId;
};

interface IResolutionDraft {
  type: ResolutionType;
  record: IInventoryShortage;
  /** Minted once per attempt and resent verbatim while the outcome is unknown. */
  operationToken: string;
  batchId?: number;
  kilograms: number | null;
  notes: string;
}

interface IInventoryShortageFilterFieldsProps {
  products: { label: string; value: number }[];
  stores: { label: string; value: number }[];
  loading: string[];
  getProducts: (keyword?: string) => void;
}

const InventoryShortageFilterFields: React.FC<
  IInventoryShortageFilterFieldsProps
> = ({ products, stores, loading, getProducts }) => {
  const { t } = useTranslation();

  return (
    <Row gutter={[10, 0]}>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="keyword"
          label={t("inventory_shortage_keyword")}
          width="large"
          input={{ type: "text" }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="status"
          label={t("status")}
          width="large"
          input={{
            type: "select",
            options: [
              InventoryShortageStatus.Open,
              InventoryShortageStatus.PartiallyCovered,
              InventoryShortageStatus.Covered,
              InventoryShortageStatus.Cancelled,
              InventoryShortageStatus.SettledAsVariance,
            ].map((status) => ({
              label: t(getInventoryShortageStatusLabel(status)),
              value: status,
            })),
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="productId"
          label={t("select_product")}
          width="large"
          input={{
            type: "select-pagination",
            allowSearch: true,
            loading: loading.includes("products"),
            handleSearch: getProducts,
            options: products,
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="storeId"
          label={t("select_store")}
          width="large"
          input={{
            type: "select",
            allowSearch: true,
            loading: loading.includes("stores"),
            options: stores,
          }}
        />
      </Col>
      <Col xs={24} sm={12} md={8} lg={6}>
        <FieldBuilder
          name="unresolvedOnly"
          label={t("inventory_shortage_unresolved_only")}
          width="large"
          input={{
            type: "select",
            options: [
              { label: t("yes"), value: true },
              { label: t("no"), value: false },
            ],
          }}
        />
      </Col>
    </Row>
  );
};

const InventoryShortagePage = () => {
  const { t, i18n } = useTranslation();
  const { direction } = useContext(AppContext);
  const [query, setQuery] =
    useState<IInventoryShortageQuery>(buildEmptyQuery());
  const [list, setList] = useState<IBaseListingResponse<IInventoryShortage>>();
  const [loading, setLoading] = useState<string[]>([]);
  const [stores, setStores] = useState<{ label: string; value: number }[]>([]);
  const [products, setProducts] = useState<{ label: string; value: number }[]>(
    [],
  );
  const [draft, setDraft] = useState<IResolutionDraft>();
  const [batchOptions, setBatchOptions] = useState<IResolutionBatchOption[]>(
    [],
  );
  const [saving, setSaving] = useState(false);
  // Discards a batch list that arrives after its dialog was closed or replaced.
  const batchRequestIdRef = useRef(0);

  // Permissions are deliberately not read from localStorage: `canCover` and
  // `canCorrectReceipt` on each row already fold in the permission, the
  // remaining balance and the order state. A second local check could only
  // disable a button the server was willing to accept.

  const weightLabels = useMemo(
    () => ({
      gram: t("cash_check_unit_gram"),
      kilogram: t("cash_check_unit_kilogram"),
    }),
    [t, i18n.language],
  );

  const showWeight = (grams: unknown) =>
    formatWeightFromGrams(grams, weightLabels);

  const setLoadingKey = (key: string, value: boolean) => {
    setLoading((current) =>
      value
        ? current.includes(key)
          ? current
          : [...current, key]
        : current.filter((item) => item !== key),
    );
  };

  const getData = async () => {
    try {
      setLoadingKey("list", true);
      const { data } = await EndPoints.wmsInventoryShortage.getAll(query);
      setList(normalizeInventoryShortageListingResponse(data));
    } catch {
      setList({
        data: [],
        total: 0,
        currentPage: query.page ?? 1,
        lastPage: 1,
      });
      message.error(t("inventory_shortages_load_failed"));
    } finally {
      setLoadingKey("list", false);
    }
  };

  const getStores = async () => {
    try {
      setLoadingKey("stores", true);
      const { data } = await EndPoints.wmsShared.getDropdownStores();
      setStores(
        (data?.data ?? []).map((store: any) => ({
          label: getLocalizedName(store.name, i18n.language),
          value: store.id,
        })),
      );
    } catch {
      setStores([]);
    } finally {
      setLoadingKey("stores", false);
    }
  };

  const getProducts = async (keyword?: string) => {
    try {
      setLoadingKey("products", true);
      const { data } = await EndPoints.product.getAllProducts({
        light: true,
        page: 1,
        perPage: 50,
        keyword: keyword || undefined,
      });
      setProducts(
        (data?.data ?? []).map((product: any) => ({
          label: product.name,
          value: product.id,
        })),
      );
    } catch {
      setProducts([]);
    } finally {
      setLoadingKey("products", false);
    }
  };

  useEffect(() => {
    getStores();
    getProducts();
  }, [direction, i18n.language]);

  useEffect(() => {
    getData();
  }, [query, direction]);

  const resetFilters = () => {
    setQuery(buildEmptyQuery(query.perPage));

    const form = document.getElementById("inventory-shortage-filter");
    if (form) {
      form.dispatchEvent(
        new Event("reset", { cancelable: true, bubbles: true }),
      );
    }
  };

  const submitFilters = (data: any) => {
    setQuery({
      page: 1,
      perPage: query.perPage,
      status: toNumberOrUndefined(data.status),
      productId: toNumberOrUndefined(data.productId),
      storeId: toNumberOrUndefined(data.storeId),
      keyword: data.keyword?.trim() || undefined,
      unresolvedOnly:
        data.unresolvedOnly === true || data.unresolvedOnly === "true"
          ? true
          : undefined,
    });
  };

  /** Every batch of this product and store, FEFO-ordered by the endpoint — no
   * `orders[]` of our own, which would override it. */
  const loadStoreBatches = async (
    record: IInventoryShortage,
    type: ResolutionType,
  ): Promise<IResolutionBatchOption[]> => {
    if (!record.productId || !record.storeId) return [];

    try {
      setLoadingKey("batches", true);
      const { data } = await EndPoints.wmsBatch.getAllBatches({
        productId: record.productId,
        storeId: record.storeId,
        // A cover draws stock out of the batch, so an empty one could only be
        // refused. A correction rewrites what the batch was received with, and
        // a batch already drawn down to zero is exactly the one that needs it.
        hasStock: type === "cover" ? true : undefined,
        perPage: -1,
      });
      return (data?.data ?? []).map(fromStoreBatch);
    } catch {
      message.error(t("inventory_shortage_batches_load_failed"));
      return [];
    } finally {
      setLoadingKey("batches", false);
    }
  };

  const selectBatch = (batchId: number) =>
    setDraft((previous) => (previous ? { ...previous, batchId } : previous));

  const openDraft = async (
    type: ResolutionType,
    record: IInventoryShortage,
  ) => {
    const requestId = ++batchRequestIdRef.current;
    setBatchOptions([]);
    setDraft({
      type,
      record,
      operationToken: createUuidV4(),
      batchId: undefined,
      kilograms: null,
      notes: "",
    });

    const allocated = record.receiptCorrectionBatches;

    // A correction rewrites where the goods came from, so it is restricted to
    // the batches this line was allocated from — but only while that list is
    // non-empty. With no allocations there is nothing to contradict and the
    // server restricts nothing either, so the store list serves both dialogs.
    if (type === "correction" && allocated.length) {
      setBatchOptions(allocated.map(fromAllocatedBatch));
      if (allocated.length === 1) selectBatch(allocated[0].batchId);
      return;
    }

    const storeBatches = await loadStoreBatches(record, type);
    if (requestId !== batchRequestIdRef.current) return;

    setBatchOptions(storeBatches);
    const preselected =
      type === "cover"
        ? pickDefaultCoverBatch(allocated, storeBatches)
        : storeBatches.length === 1
          ? storeBatches[0].batchId
          : undefined;
    if (preselected !== undefined) selectBatch(preselected);
  };

  const closeDraft = () => {
    batchRequestIdRef.current += 1;
    setDraft(undefined);
    setBatchOptions([]);
  };

  const selectedBatch = batchOptions.find(
    (batch) => batch.batchId === draft?.batchId,
  );

  const batchesLoading = loading.includes("batches");

  // A correction restricted to the line's own allocations is a different
  // question from a cover free to name any batch in the store, so the field
  // says which one is being asked.
  const restrictedToAllocations =
    draft?.type === "correction" &&
    draft.record.receiptCorrectionBatches.length > 0;
  const batchFieldLabelKey = restrictedToAllocations
    ? "inventory_shortage_source_batch"
    : "inventory_shortage_store_batch";
  const batchFieldHintKey = restrictedToAllocations
    ? "inventory_shortage_source_batch_hint"
    : draft?.type === "cover"
      ? "inventory_shortage_store_batch_hint"
      : "inventory_shortage_store_batch_correction_hint";

  /** `allocatedQuantityPhysical` only exists on the allocation list, so it
   * appears for a correction and is simply absent for a cover. */
  const renderBatchLabel = (batch: IResolutionBatchOption) =>
    [
      batch.batchNumber || `#${batch.batchId}`,
      `${t("purchase_date")}: ${batch.purchaseDate ?? "-"}`,
      batch.expirationDate
        ? moment(batch.expirationDate).format("YYYY-MM-DD")
        : undefined,
      `${t("inventory_shortage_batch_available")}: ${showWeight(
        batch.availableQuantityPhysical,
      )}`,
      batch.allocatedQuantityPhysical !== undefined
        ? `${t("inventory_shortage_batch_allocated")}: ${showWeight(
            batch.allocatedQuantityPhysical,
          )}`
        : undefined,
    ]
      .filter(Boolean)
      .join(" — ");

  /**
   * A cover moves stock out of the batch, so it cannot exceed what the batch
   * still holds. A receipt correction asserts the batch was received heavier
   * than recorded, so current availability is irrelevant to it.
   */
  const maxGrams = (() => {
    if (!draft) return 0;
    const remaining = draft.record.remainingQuantityPhysical;
    if (draft.type === "correction") return remaining;
    return Math.min(remaining, selectedBatch?.availableQuantityPhysical ?? 0);
  })();

  const draftGrams = kilogramsToGrams(draft?.kilograms);
  const notesTooShort =
    draft?.type === "correction" &&
    draft.notes.trim().length < INVENTORY_SHORTAGE_NOTES_MIN_LENGTH;
  const canSubmitDraft = Boolean(
    draft &&
      draft.batchId !== undefined &&
      draftGrams !== undefined &&
      draftGrams >= 1 &&
      draftGrams <= maxGrams &&
      !notesTooShort,
  );

  const reportResolutionError = (error: any) => {
    const errorKey = getErrorKey(error);
    const context = getErrorContext(error);

    if (!errorKey) {
      // Plain Laravel validation (missing batch, short notes) or an unknown fault.
      message.error(getErrorText(error) ?? t("operation_failed"));
      return;
    }

    // The server text is authoritative; the mapped key only fills a gap and
    // carries the quantities that make the refusal actionable.
    const fallback = t(getInventoryShortageErrorLabel(errorKey), {
      remaining: showWeight(context.remaining),
      requested: showWeight(context.requested),
      available: showWeight(context.available),
    });

    message.error(getErrorText(error) ?? fallback);

    if (errorKey === InventoryShortageErrorKey.IntegrityFailed) {
      message.warning(t("inventory_shortage_error_integrity_failed_hint"));
    }
  };

  const applyResolvedRow = (resolved: IInventoryShortage) =>
    setList((previous) =>
      previous
        ? {
            ...previous,
            data: previous.data.map((row) =>
              row.id === resolved.id ? resolved : row,
            ),
          }
        : previous,
    );

  const submitDraft = async () => {
    if (!draft || !canSubmitDraft || draftGrams === undefined) return;

    const payload = {
      batchId: draft.batchId as number,
      quantityPhysical: draftGrams,
      operationToken: draft.operationToken,
      notes: draft.notes.trim() || undefined,
    };

    const send = () =>
      draft.type === "cover"
        ? EndPoints.wmsInventoryShortage.cover(draft.record.id, payload, {
            suppressErrorNotification: true,
          })
        : EndPoints.wmsInventoryShortage.correctReceipt(
            draft.record.id,
            { ...payload, notes: draft.notes.trim() },
            { suppressErrorNotification: true },
          );

    try {
      setSaving(true);
      let response;
      try {
        response = await send();
      } catch (initialError: any) {
        if (!isUndecidedRequestError(initialError)) throw initialError;
        // The outcome is unknown, so replay the identical token: the server
        // returns the original result instead of applying the change twice.
        response = await send();
      }

      message.success(
        draft.type === "cover"
          ? t("inventory_shortage_covered_successfully")
          : t("inventory_shortage_receipt_corrected_successfully"),
      );
      closeDraft();

      // Both endpoints answer with the full resource as it stands after the
      // operation, so the row is replaced from the server's own state — not
      // patched optimistically. Only the list order can go stale, and the
      // refresh button restores it.
      const resolved = mapInventoryShortageResource(response?.data);
      if (resolved?.id) {
        applyResolvedRow(resolved);
      } else {
        await getData();
      }
    } catch (error: any) {
      reportResolutionError(error);

      if (isUndecidedRequestError(error)) {
        // Still no verdict after the replay. Keeping the token is what makes a
        // manual retry safe — a fresh one could apply the operation twice.
        message.warning(t("inventory_shortage_retry_hint"));
      } else {
        // A decided refusal: nothing was applied, and the next attempt is a new
        // operation. Reusing this token with edited values would be a conflict.
        setDraft((previous) =>
          previous ? { ...previous, operationToken: createUuidV4() } : previous,
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const renderQuantity = (grams: number, strong = false) => (
    <Typography.Text strong={strong}>{showWeight(grams)}</Typography.Text>
  );

  /**
   * The weighing this shortage came out of. The scanned barcode is the only
   * thread back to the cash-check screen, so it is shown verbatim — never
   * reformatted, and never parsed for a decision.
   */
  const renderSourceVerification = (record: IInventoryShortage) => {
    const verification = record.sourceVerification;
    const method = verification?.method?.value as
      | CashCheckVerificationMethod
      | undefined;
    if (!verification || method === undefined) return "-";

    return (
      <Space direction="vertical" size={0}>
        <Tag color={getCashCheckVerificationMethodColor(method)}>
          {verification.method?.name ??
            t(getCashCheckVerificationMethodName(method))}
        </Tag>
        {verification.scannedBarcode && (
          <Typography.Text
            type="secondary"
            copyable={{ text: verification.scannedBarcode }}
            className={styles.scannedBarcode}
          >
            {verification.scannedBarcode}
          </Typography.Text>
        )}
        {verification.measuredQuantityPhysical !== null &&
          verification.measuredQuantityPhysical !== undefined && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("inventory_shortage_measured_quantity")}:{" "}
              {showWeight(verification.measuredQuantityPhysical)}
            </Typography.Text>
          )}
      </Space>
    );
  };

  const renderCoverages = (record: IInventoryShortage) => {
    if (!record.coverages.length) return "-";

    return (
      <ul className={styles.coverageList}>
        {record.coverages.map((coverage, index) => (
          <li key={coverage.id ?? index}>
            <Typography.Text type="secondary">
              {t(getInventoryShortageCoverageTypeLabel(coverage.type?.value))}:{" "}
              {showWeight(coverage.quantityPhysical)}
              {coverage.batchNumber ? ` — ${coverage.batchNumber}` : ""}
            </Typography.Text>
          </li>
        ))}
      </ul>
    );
  };

  const columns: ColumnProps<IInventoryShortage>[] = [
    {
      title: t("inventory_shortage_id"),
      dataIndex: "id",
      key: "id",
      width: 90,
      align: "center",
    },
    {
      title: t("order_number"),
      key: "orderNumber",
      width: 130,
      align: "center",
      render: (_, record) =>
        record.order?.number ? (
          <Link to={`/orders/details/${record.order.id}`}>
            {record.order.number}
          </Link>
        ) : (
          "-"
        ),
    },
    {
      title: t("product"),
      key: "product",
      width: 230,
      render: (_, record) =>
        record.product?.id ? (
          <Link to={`/products/details/${record.product.id}`}>
            {getLocalizedName(record.product.name, i18n.language)}
          </Link>
        ) : (
          getLocalizedName(record.product?.name, i18n.language)
        ),
    },
    {
      title: t("store"),
      key: "store",
      width: 200,
      render: (_, record) =>
        getLocalizedName(record.store?.name, i18n.language),
    },
    {
      title: t("inventory_shortage_source_verification"),
      key: "sourceVerification",
      width: 190,
      align: "center",
      render: (_, record) => renderSourceVerification(record),
    },
    {
      title: t("inventory_shortage_initial_quantity"),
      key: "initialQuantityPhysical",
      width: 140,
      align: "center",
      render: (_, record) => renderQuantity(record.initialQuantityPhysical),
    },
    {
      title: t("inventory_shortage_covered_quantity"),
      key: "coveredQuantityPhysical",
      width: 140,
      align: "center",
      render: (_, record) => renderQuantity(record.coveredQuantityPhysical),
    },
    {
      title: t("inventory_shortage_corrected_quantity"),
      key: "correctedQuantityPhysical",
      width: 150,
      align: "center",
      render: (_, record) => renderQuantity(record.correctedQuantityPhysical),
    },
    {
      title: t("inventory_shortage_remaining_quantity"),
      key: "remainingQuantityPhysical",
      width: 140,
      align: "center",
      render: (_, record) => (
        <span className={styles.remaining}>
          {renderQuantity(record.remainingQuantityPhysical, true)}
        </span>
      ),
    },
    {
      title: t("status"),
      dataIndex: "status",
      key: "status",
      width: 160,
      align: "center",
      render: (status) => (
        <Tag color={getInventoryShortageStatusColor(status?.value)}>
          {t(getInventoryShortageStatusLabel(status?.value))}
        </Tag>
      ),
    },
    {
      title: t("inventory_shortage_coverages"),
      key: "coverages",
      width: 260,
      render: (_, record) => renderCoverages(record),
    },
    {
      title: t("created_at"),
      key: "createdAt",
      width: 170,
      align: "center",
      render: (_, record) =>
        record.createdAt
          ? moment(record.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "-",
    },
    {
      title: t("actions"),
      key: "actions",
      fixed: "right",
      width: 110,
      align: "center",
      render: (_, record) => {
        // `canSettle` is deliberately absent: the endpoint always refuses, so
        // no settle control is rendered at all.
        if (!record.canCover && !record.canCorrectReceipt) {
          return (
            <Typography.Text
              type="secondary"
              className={styles.unavailableReason}
            >
              {t(
                getInventoryShortageUnavailableReasonLabel(
                  record.resolutionUnavailableReason,
                ),
              )}
            </Typography.Text>
          );
        }

        // Nothing else gates these buttons. In particular not the length of
        // `receiptCorrectionBatches`: a cover reads a different list entirely,
        // and a correction falls back to that same list when this one is empty.
        return (
          <Space size={4} className={styles.actionButtons}>
            {record.canCover && (
              <Tooltip title={t("inventory_shortage_cover_action")}>
                <Button
                  type="primary"
                  size="small"
                  shape="circle"
                  icon={<PlusOutlined />}
                  aria-label={t("inventory_shortage_cover_action")}
                  onClick={() => openDraft("cover", record)}
                />
              </Tooltip>
            )}
            {record.canCorrectReceipt && (
              <Tooltip title={t("inventory_shortage_correct_receipt_action")}>
                <Button
                  size="small"
                  shape="circle"
                  icon={<EditOutlined />}
                  aria-label={t("inventory_shortage_correct_receipt_action")}
                  onClick={() => openDraft("correction", record)}
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div dir={direction} className={styles.container}>
      <PageHeader
        title={t("wms_inventory_shortages")}
        subTitle={t("wms_inventory_shortages_management")}
        extra={[
          <RefreshBtn
            key="refresh"
            loading={loading.includes("list")}
            onClick={getData}
          >
            {t("refresh")}
          </RefreshBtn>,
          <Divider key="divider" type="vertical" />,
          <Button
            key="reset"
            danger
            icon={<ClearOutlined />}
            onClick={resetFilters}
            loading={loading.includes("list")}
          >
            {t("reset")}
          </Button>,
        ]}
      />

      <Alert
        className={styles.notice}
        type="warning"
        showIcon
        message={t("inventory_shortage_notice_title")}
        description={t("inventory_shortage_notice_description")}
      />

      <SearchFilterCard
        formId="inventory-shortage-filter"
        searchValue={undefined}
        setSearchValue={() => undefined}
        onReset={resetFilters}
        resetLoading={loading.includes("list")}
        applyLoading={loading.includes("list")}
        visiableSearch
      >
        <Form<IInventoryShortageQuery>
          formId="inventory-shortage-filter"
          defaultValues={query}
          onSubmit={submitFilters}
        >
          <InventoryShortageFilterFields
            products={products}
            stores={stores}
            loading={loading}
            getProducts={getProducts}
          />
        </Form>
      </SearchFilterCard>

      <Table<IInventoryShortage>
        rowKey="id"
        showPagination
        columns={columns}
        dataSource={list?.data ?? []}
        loading={loading.includes("list") || list === undefined}
        total={list?.total}
        pageSize={query.perPage}
        page={query.page}
        scroll={{ x: 2200 }}
        onPaginationChange={(page, perPage) => {
          setQuery((current) => ({ ...current, page, perPage }));
        }}
      />

      <Modal
        title={
          draft?.type === "cover"
            ? t("inventory_shortage_cover_title")
            : t("inventory_shortage_correct_receipt_title")
        }
        open={Boolean(draft)}
        onCancel={closeDraft}
        onOk={submitDraft}
        okText={
          draft?.type === "cover"
            ? t("inventory_shortage_cover_action")
            : t("inventory_shortage_correct_receipt_action")
        }
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !canSubmitDraft }}
        confirmLoading={saving}
        width={620}
        destroyOnClose
      >
        {draft && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert
              type={draft.type === "cover" ? "info" : "warning"}
              showIcon
              message={
                draft.type === "cover"
                  ? t("inventory_shortage_cover_notice")
                  : t("inventory_shortage_correct_receipt_notice")
              }
            />

            <Typography.Text type="secondary">
              {t("inventory_shortage_remaining_quantity")}:{" "}
              <Typography.Text strong>
                {showWeight(draft.record.remainingQuantityPhysical)}
              </Typography.Text>
            </Typography.Text>

            <div>
              <Typography.Text>{t(batchFieldLabelKey)}</Typography.Text>
              <Select
                style={{ width: "100%", marginTop: 4 }}
                value={draft.batchId}
                placeholder={t(batchFieldLabelKey)}
                loading={batchesLoading}
                disabled={batchesLoading}
                showSearch
                optionFilterProp="label"
                onChange={(batchId) =>
                  setDraft((previous) =>
                    previous ? { ...previous, batchId } : previous,
                  )
                }
                options={batchOptions.map((batch) => ({
                  value: batch.batchId,
                  label: renderBatchLabel(batch),
                }))}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t(batchFieldHintKey)}
              </Typography.Text>
              {!batchesLoading && !batchOptions.length && (
                <Alert
                  style={{ marginTop: 8 }}
                  type="warning"
                  showIcon
                  message={t(
                    draft.type === "cover"
                      ? "inventory_shortage_no_batches_in_store"
                      : "inventory_shortage_no_correction_batches",
                  )}
                />
              )}
            </div>

            <div>
              <Typography.Text>
                {t("quantity")} ({t("cash_check_unit_kilogram")})
              </Typography.Text>
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                min={0}
                step={0.001}
                precision={3}
                value={draft.kilograms}
                disabled={draft.batchId === undefined}
                onChange={(kilograms) =>
                  setDraft((previous) =>
                    previous ? { ...previous, kilograms } : previous,
                  )
                }
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {draftGrams !== undefined
                  ? `= ${draftGrams.toLocaleString()} ${t("cash_check_unit_gram")} — `
                  : ""}
                {t("inventory_shortage_max_quantity")}: {showWeight(maxGrams)}
              </Typography.Text>
              {draft.type === "cover" &&
                selectedBatch &&
                selectedBatch.availableQuantityPhysical <
                  draft.record.remainingQuantityPhysical && (
                  <Alert
                    style={{ marginTop: 8 }}
                    type="warning"
                    showIcon
                    message={t("inventory_shortage_batch_short_hint")}
                  />
                )}
            </div>

            <div>
              <Typography.Text>
                {t("inventory_shortage_notes")}
                {draft.type === "correction" ? " *" : ""}
              </Typography.Text>
              <Input.TextArea
                style={{ marginTop: 4 }}
                rows={3}
                maxLength={INVENTORY_SHORTAGE_NOTES_MAX_LENGTH}
                showCount
                value={draft.notes}
                placeholder={
                  draft.type === "correction"
                    ? t("inventory_shortage_notes_required_hint")
                    : t("inventory_shortage_notes")
                }
                onChange={(event) =>
                  setDraft((previous) =>
                    previous
                      ? { ...previous, notes: event.target.value }
                      : previous,
                  )
                }
              />
              {notesTooShort && (
                <Typography.Text type="danger" style={{ fontSize: 12 }}>
                  {t("inventory_shortage_notes_required_hint")}
                </Typography.Text>
              )}
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default InventoryShortagePage;
